<template>
  <div>
    <CheckoutBillingAddressForm
      :address="formValues"
      :api-errors
      :countries
      :submitting="submitting || loading"
      :with-user-information="userInformationStore.isFormRequired"
      @submit="handleSubmit"
      @submit-failed="handleFailure"
    />

    <ReassuranceItems class="mt-24 md:mt-56" :selected-mobile-plan />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { Bill } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import ReassuranceItems from '@backmarket/nuxt-layer-checkout/components/ReassuranceItems/ReassuranceItems.vue'
import { useAddressStore } from '@backmarket/nuxt-layer-checkout/stores/addressStore'
import { useUserInformationStore } from '@backmarket/nuxt-layer-checkout/stores/userInformationStore'
import { getSelectedMobilePlan } from '@backmarket/nuxt-layer-checkout/utils/mobilePlan/getSelectedMobilePlan'
import { useLogUnhandledAPIErrors } from '@backmarket/nuxt-module-address/useLogUnhandledAPIErrors'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import type { ErrorsOf } from '@ds/components/Form'

import useHandleUnauthorizedUser from '~/scopes/checkout/composables/useHandleUnauthorizedUser'
import { useSaveBouyguesAddress } from '~/scopes/checkout/composables/useSaveBouyguesAddress'
import { getAPIErrors } from '~/scopes/checkout/utils/getAPIErrors'

import type { FormValues } from './components/BillingAddressForm.types'
import CheckoutBillingAddressForm from './components/BillingAddressForm.vue'

const tracking = useTracking()
const logger = useLogger()

const addressStore = useAddressStore()
const userInformationStore = useUserInformationStore()

const { openErrorToast } = useTheToast()
const { logUnhandledAPIErrors } = useLogUnhandledAPIErrors()
const { handleUnauthorizedUser } = useHandleUnauthorizedUser()
const { saveBouyguesAddress } = useSaveBouyguesAddress()

const apiErrors = ref<{ [key: string]: string }>({})
const submitting = ref(false)

const props = defineProps<{
  loading: boolean
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  billableCountries: ApiSpecs['/bm/cart']['GET']['response']['billableCountries']
}>()

const selectedMobilePlan = computed(() => {
  return getSelectedMobilePlan(props.items)
})

const defaultAddress: Bill = {
  birthdate: null,
  city: null,
  company: null,
  country: null,
  countryDialInCode: null,
  customerIdNumber: null,
  dateCreation: null,
  dateModification: null,
  email: null,
  firstName: null,
  firstNamePronunciation: null,
  lastName: null,
  lastNamePronunciation: null,
  phone: null,
  postalCode: null,
  stateOrProvince: null,
  street: null,
  street2: null,
}

const formValues = computed(() => {
  if (!props.addressList.bill) {
    return defaultAddress
  }

  return {
    ...props.addressList.bill,
    ...insertIf(userInformationStore.isFormRequired, {
      birthdate: userInformationStore.birthdate || '',
      nationalId: userInformationStore.nationalId,
    }),
  }
})

const countries = computed(() => {
  return [...props.billableCountries]
    .map(({ name, countryCode: billableCountryCode }) => ({
      value: billableCountryCode,
      label: name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

const emit = defineEmits<{
  (e: 'update'): void
}>()

const handleFailure = (errors: ErrorsOf<Omit<FormValues, 'nationalId'>>) => {
  logger.info('[CHECKOUT] Failed to submit billing form', {
    errors: { fields: Object.keys(errors) },
    owners: ['bot-squad-checkout-front'],
  })
  tracking.trackClick({
    name: 'billing_address_saved_failure',
    zone: 'address',
  })
}

const handleSubmit = async (values: Omit<FormValues, 'nationalId'>) => {
  apiErrors.value = {}
  submitting.value = true

  try {
    // TODO: check why we rely on shipping address to get the country dial in code and phone
    const address = {
      ...(props.addressList.bill || defaultAddress),
      ...values,
      countryDialInCode: props.addressList.deliver?.countryDialInCode || null,
      phone: props.addressList.deliver?.phone || null,
    }

    if (selectedMobilePlan.value?.type === 'ONLINE') {
      await saveBouyguesAddress(address, 'BILLING')
    }

    // 1. Save address
    await addressStore.saveAddress({
      address,
      formType: userInformationStore.formType,
      isShipping: false,
      isBilling: true,
    })

    tracking.trackClick({
      name: 'billing_address_saved',
      zone: 'address',
    })

    emit('update')
    submitting.value = false
  } catch (error) {
    tracking.trackClick({
      name: 'billing_address_saved_failure',
      zone: 'address',
    })

    if (
      (error as Record<string, unknown>)?.status === 400 ||
      (error as Record<string, unknown>)?.status === 422
    ) {
      const errors = getAPIErrors(error as Record<string, unknown>)
      logUnhandledAPIErrors(Object.keys(errors))
      apiErrors.value = errors
      openErrorToast()
    } else
      await handleUnauthorizedUser(
        error as Record<string, unknown>,
        '[CHECKOUT] Failed to submit billing form',
      )

    submitting.value = false
  }
}
</script>
